<template>
  <div class="flex flex-col gap-2.5">
    <div class="rounded-[12px] border border-[#ebedef]" :class="usageCardClass">
      <Progress
        v-if="showBar"
        :percentage="percentage"
        size="sm"
        :color="barColor"
        track-color="var(--color-gray-200)"
        class="mb-1.5"
      />
      <div class="font-medium text-gray-800" :class="countClass">
        {{ countLabel }}
      </div>
      <div class="text-[14px] leading-[18px] text-gray-500">Registrations</div>
      <div
        v-if="cardSubtitle"
        class="mt-1 text-[14px] leading-[18px] text-gray-500"
      >
        {{ cardSubtitle }}
      </div>
    </div>

    <div
      v-if="showAmounts"
      class="rounded-[12px] border border-[#ebedef]"
      :class="amountCardClass"
    >
      <template v-if="showOverdueAmount">
        <div class="flex items-start justify-between gap-2">
          <span class="text-[14px] leading-[18px] text-gray-500">
            Amount past due
          </span>
          <button
            type="button"
            class="text-link text-[14px] leading-[18px]"
            @click="emit('navigate', '/settings/billing')"
          >
            View statement
          </button>
        </div>
        <div class="mt-1 text-[18px] leading-[22px] font-medium text-red-700">
          {{ overdueAmount }}
        </div>
      </template>

      <div v-if="showRegistrationAmount" :class="showOverdueAmount && 'mt-2.5'">
        <span class="text-[14px] leading-[18px] text-gray-500">
          Amount due
        </span>
        <div class="mt-1 text-[18px] leading-[22px] font-medium text-gray-800">
          {{ registrationAmount }}
        </div>
        <div
          v-if="registrationSubtitle"
          class="mt-1.5 text-[13px] leading-[16px] text-gray-500 italic"
        >
          {{ registrationSubtitle }}
        </div>
      </div>
    </div>

    <Alert v-if="shownBanner" :variant="shownBanner.variant" :icon="AlertIcon">
      {{ shownBanner.text }}
    </Alert>

    <Button
      v-if="cta"
      variant="primary"
      :size="buttonSize"
      class="w-full"
      @click="emit('action', cta.action)"
    >
      {{ cta.label }}
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Alert, Button, Progress } from '@dacr/library'
import { AlertIcon } from '@dacr/icons'

import { useMenuModel, type MenuBanner, type MenuCta } from './useMenuModel'

const props = defineProps<{ dense?: boolean }>()

const emit = defineEmits<{
  action: [action: MenuCta['action']]
  navigate: [path: string]
}>()

const {
  tier,
  showBar,
  percentage,
  barColor,
  countLabel,
  cardSubtitle,
  banner,
  cta,
  billingFailure,
  showAmounts,
  showOverdueAmount,
  showRegistrationAmount,
  overdueAmount,
  registrationAmount,
  registrationSubtitle,
  billingFailureText,
} = useMenuModel()

const shownBanner = computed<MenuBanner | null>(() => {
  if (tier.value === 'pro' && billingFailure.value) {
    return { variant: 'error', text: billingFailureText.value }
  }
  return banner.value
})

const usageCardClass = computed(() => {
  if (props.dense) return showBar.value ? 'px-3.5 pt-3 pb-2' : 'px-3.5 py-2'
  return showBar.value ? 'px-4 pt-3.5 pb-2.5' : 'px-4 py-2.5'
})

const amountCardClass = computed(() =>
  props.dense ? 'px-3.5 py-2' : 'px-4 py-2.5'
)

const countClass = computed(() =>
  props.dense ? 'text-[18px] leading-[22px]' : 'text-[22px] leading-[26px]'
)

const buttonSize = computed<'md' | 'lg'>(() => (props.dense ? 'md' : 'lg'))
</script>
